import React from 'react';
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import {useAppState} from "@stores/AppStateContext";

import {useNotification} from '../../hooks/useNotification';
import {HeaderMenu} from '../HeaderMenu/HeaderMenu';
import {MobileHeader} from "./MobileHeader";

import {ModalManager} from '../../screens/modals/ModalManager';
import {CheckoutModal} from "../../screens/modals/payments/CheckoutModal";
import {MainContextMenuManager} from "../context-menu/MainContextMenuManager";
import {LayoutAfterFreeTrial} from "./LayoutAfterFreeTrial";
import {isFreeTrialExpired} from "@stores/client";
import {Loading} from '../../components/Loading';
import {PageSurface} from './PageSurface';
import {LayoutWithoutWorkspace} from "./LayoutWithoutWorkspace";
import {MainSidebar, getSidebarCurrentWidth} from "../MainSidebar/MainSidebar";
import {SettingsSecondaryNav, SETTINGS_SECONDARY_NAV_WIDTH} from "../SettingsSecondaryNav/SettingsSecondaryNav";
import {MainContentRouter} from "./MainContentRouter";

import {screenSizeProvider} from "@stores/ScreenSizeProvider";
import {settingsPages} from "@stores/ViewStore";
import {MODALS} from "../../screens/modals/modal-names";
import {snackBarRef} from "@stores/snackbarRef";


import './layout.css';


export const Layout = observer(() => {
    const appState = useAppState();
    /* hack to be able to initiate snackbar globally */
    snackBarRef.current = useNotification();
    const isMobile = screenSizeProvider.view !== 'desktop';
    const globalState = appState;
    const rootStore = globalState.rootStore;
    const userState = globalState.userState;
    const sidebarWidth = isMobile ? 0 : getSidebarCurrentWidth(rootStore);

    const isSettingsPage = settingsPages.includes(rootStore.view.page as any);
    const secondaryNavWidth = (!isMobile && isSettingsPage) ? SETTINGS_SECONDARY_NAV_WIDTH : 0;
    const availableWidth = !isMobile ? `max(0px, calc(100vw - ${sidebarWidth + secondaryNavWidth}px))` : undefined;
    const screenStyle = !isMobile ? {
        maxWidth: availableWidth,
        flexBasis: availableWidth,
        minWidth: 0,
    } : undefined;

    const activeModal = appState.topModal;
    const headerKey = `${rootStore.currentLocale}1`;
    const headerNode = isMobile
        ? <MobileHeader key={headerKey}/>
        : <HeaderMenu key={headerKey}/>;

    let content;

    if (!rootStore.isReady) {
        return (
            <PageSurface className="layout">
                <Loading
                    key={rootStore.currentLocale}
                />
            </PageSurface>
        );
    }
    // when rootStore is ready the client data is already loaded and available
    const isOnExpiredFreeTrial = rootStore.isReady && isFreeTrialExpired(globalState.userState!.dsManager!.clientAR!.row!);
    let withoutLayout = false;
    if (isOnExpiredFreeTrial) {
        content = <LayoutAfterFreeTrial/>;
        withoutLayout = true;
    } else if (userState && userState.availableWorkspaces.length === 0) {
        content = <LayoutWithoutWorkspace/>;
        withoutLayout = true;
    } else {
        content = <MainContentRouter/>;
    }

    return (
        <>
            <div className={classNames({
                "layout": true,
                "layout--center": withoutLayout
            })}>
                {
                    withoutLayout
                        ? content
                        : (
                            <>
                                <MainSidebar/>
                                {isSettingsPage && (
                                    <SettingsSecondaryNav/>
                                )}
                                <main
                                    className="layout__screen"
                                    style={screenStyle}
                                >
                                    {headerNode}
                                    {content}
                                </main>
                            </>
                        )
                }
            </div>
            <ModalManager key={`${rootStore.currentLocale}3`}/>
            {activeModal === MODALS.CHECKOUT && (
                <CheckoutModal key={`${rootStore.currentLocale}4`}/>
            )}
            <MainContextMenuManager key={`${rootStore.currentLocale}5`}/>
        </>
    );
});
